import { useEffect, useState } from 'react'
import { Card, Col, Row, Badge } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { getPoshCommittees } from '../../../api/IncidentAPI'
import { format } from 'date-fns'

const CommitteeDisplay = () => {
  const [committees, setCommittees] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchCommittees()
  }, [])

  const fetchCommittees = async () => {
    setLoading(true)
    try {
      const response = await getPoshCommittees({
        page: 1,
        limit: 10,
      })

      if (response.statusCode === 200) {
        setCommittees(response.data.committees || [])
      } else {
        toast.error(response.message || 'Failed to fetch committees')
      }
    } catch (error) {
      console.error('Error fetching committees:', error)
      toast.error('Failed to fetch committees')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    )
  }

  if (committees.length === 0) {
    return (
      <Card className="text-center py-5">
        <Card.Body>
          <div className="fs-1 mb-3">👥</div>
          <h5 className="text-muted">No Committees Found</h5>
          <p className="text-muted">No committees have been created yet.</p>
        </Card.Body>
      </Card>
    )
  }

  return (
    <div>
      <div className="mb-4">
        <h4 className="mb-0 fw-bold">POSH Committees</h4>
        <p className="text-muted mb-0">View all POSH (Prevention of Sexual Harassment) committees and their members</p>
      </div>

      <Row>
        {committees.map((committee) => (
          <Col key={committee.id} md={12} className="mb-4">
            <Card className="border-0 shadow-sm h-100">
              <Card.Header className="bg-white border-bottom border-secondary-subtle py-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h5 className="mb-1 fw-bold text-primary">{committee.name}</h5>
                    {committee.description && (
                      <p className="text-muted mb-0 small">{committee.description}</p>
                    )}
                  </div>
                  <Badge bg={committee.isActive ? 'success' : 'secondary'} className="ms-2">
                    {committee.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                <div className="mb-3">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="fw-bold">Committee Members</span>
                    <Badge bg="primary" pill>{committee.members?.length || 0}</Badge>
                  </div>

                  {committee.members && committee.members.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover table-bordered">
                        <thead className="table-light">
                          <tr>
                            <th>Name</th>
                            <th>Employee Code</th>
                            <th>Email</th>
                            <th>Department</th>
                            <th>Designation</th>
                            <th>Member Since</th>
                          </tr>
                        </thead>
                        <tbody>
                          {committee.members.map((member: any) => (
                            <tr key={member.id}>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <div className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center text-primary fw-bold" style={{ width: '36px', height: '36px', fontSize: '0.875rem' }}>
                                    {member.user.firstName[0]}{member.user.lastName[0]}
                                  </div>
                                  <span className="fw-bold">
                                    {member.user.firstName} {member.user.lastName}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <span className="badge bg-secondary">{member.user.employeeCode || 'N/A'}</span>
                              </td>
                              <td>
                                <a href={`mailto:${member.user.email}`} className="text-decoration-none text-primary">
                                  {member.user.email}
                                </a>
                              </td>
                              <td>{member.user.department?.name || 'N/A'}</td>
                              <td>{member.user.designation?.name || 'N/A'}</td>
                              <td>
                                <small className="text-muted">
                                  {format(new Date(member.createdAt), 'MMM dd, yyyy')}
                                </small>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted">
                      <div className="fs-1 mb-2">👥</div>
                      <p className="mb-0">No members assigned to this committee</p>
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-between align-items-center pt-3 border-top">
                  <small className="text-muted">
                    Created on {format(new Date(committee.createdAt), 'MMM dd, yyyy')}
                  </small>
                  {committee.updatedAt !== committee.createdAt && (
                    <small className="text-muted">
                      Last updated {format(new Date(committee.updatedAt), 'MMM dd, yyyy')}
                    </small>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  )
}

export default CommitteeDisplay
